import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Dropdown } from '@/components/ui/select'
import { Search, ChevronLeft, ChevronRight, User, Check } from 'lucide-react'
import { apiClient } from '@/lib/api-client'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'react-hot-toast'

interface Project {
  id: number
  name: string
  description: string
  reportingPersonId: number
  reportingPerson?: {
    id: number
    name: string
    email: string
  }
  employees?: Array<{
    id: number
    name: string
    email: string
  }>
}

interface ProjectFormProps {
  isOpen: boolean
  onClose: () => void
  project?: Project | null
  onSuccess: () => void
}

interface FormData {
  name: string
  description: string
  reportingPersonId: number
  employeeIds: number[]
}

export function ProjectForm({ isOpen, onClose, project, onSuccess }: ProjectFormProps) {
  const [users, setUsers] = useState<Array<{ id: number; name: string; email: string }>>([])
  const [loading, setLoading] = useState(false)
  const [employeeSearch, setEmployeeSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([])
  const { user } = useAuth()

  const ITEMS_PER_PAGE = 10

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<FormData>()

  useEffect(() => {
    if (isOpen) {
      fetchUsers()
      if (project) {
        const employeeIds = project.employees?.map((emp: any) => emp.id) || []
        setSelectedEmployees(employeeIds)
        reset({
          name: project.name,
          description: project.description || '',
          reportingPersonId: project.reportingPersonId,
          employeeIds: employeeIds
        })
      } else {
        setSelectedEmployees([])
        reset({
          name: '',
          description: '',
          reportingPersonId: user?.id || 0,
          employeeIds: []
        })
      }
    }
  }, [isOpen, project, reset, user])

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users')
      setUsers(response.data)
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Failed to fetch users')
    }
  }

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      let savedProject
      
      if (project) {
        // Update project
        const response = await apiClient.put(`/projects/${project.id}`, {
          name: data.name,
          description: data.description,
          reportingPersonId: data.reportingPersonId
        })
        savedProject = response.data
        
        // Handle employee assignments for existing project
        const currentEmployeeIds = project.employees?.map((emp: any) => emp.id) || []
        const newEmployeeIds = selectedEmployees
        
        // Remove employees that are no longer assigned
        for (const employeeId of currentEmployeeIds) {
          if (!newEmployeeIds.includes(employeeId)) {
            await apiClient.delete(`/projects/${project.id}/remove-employee/${employeeId}`)
          }
        }
        
        // Add new employees
        for (const employeeId of newEmployeeIds) {
          if (!currentEmployeeIds.includes(employeeId)) {
            await apiClient.post(`/projects/${project.id}/assign-employee`, { employeeId })
          }
        }
        
        toast.success('Project updated successfully')
      } else {
        // Create project
        const response = await apiClient.post('/projects', {
          name: data.name,
          description: data.description,
          reportingPersonId: data.reportingPersonId
        })
        savedProject = response.data
        
        // Assign employees to new project
        if (selectedEmployees.length > 0) {
          for (const employeeId of selectedEmployees) {
            await apiClient.post(`/projects/${savedProject.id}/assign-employee`, { employeeId })
          }
        }
        
        toast.success('Project created successfully')
      }
      
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error saving project:', error)
      toast.error(error.response?.data?.message || 'Failed to save project')
    } finally {
      setLoading(false)
    }
  }

  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`
  }))

  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(employeeSearch.toLowerCase())
  )

  // Paginate filtered users
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex)

  // Handle employee selection
  const handleEmployeeToggle = (employeeId: number) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    )
  }

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [employeeSearch])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={project ? 'Edit Project' : 'Create New Project'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground required">
            Project Name
          </label>
          <Input
            {...register('name', { required: 'Project name is required' })}
            placeholder="Enter project name"
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground">
            Description
          </label>
          <textarea
            {...register('description')}
            placeholder="Enter project description"
            rows={3}
            className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px]"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground required">
            Reporting Person
          </label>
          <Controller
            name="reportingPersonId"
            control={control}
            rules={{ required: 'Reporting person is required' }}
            render={({ field }) => (
              <Dropdown
                value={field.value ? String(field.value) : ''}
                placeholder="Select a reporting person"
                onChange={(value) => field.onChange(Number(value))}
                options={userOptions.map((option) => ({
                  value: String(option.value),
                  label: option.label,
                }))}
              />
            )}
          />
          {errors.reportingPersonId && (
            <p className="text-sm text-destructive mt-1">{errors.reportingPersonId.message}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground">
            Assign Employees
          </label>
          <div className="space-y-3">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Selected Employees Count */}
            {selectedEmployees.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-green-600" />
                <span>{selectedEmployees.length} employee(s) selected</span>
              </div>
            )}

            {/* Employee List */}
            <div className="border border-input rounded-md">
              <div className="max-h-48 overflow-y-auto">
                {paginatedUsers.length > 0 ? (
                  <div className="divide-y divide-border">
                    {paginatedUsers.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center space-x-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(user.id)}
                          onChange={() => handleEmployeeToggle(user.id)}
                          className="rounded border-input text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        />
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {employeeSearch ? 'No employees found matching your search' : 'No employees available'}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} employees
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs px-2">
                      {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : project ? 'Update Project' : 'Create Project'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
